import React, { useReducer } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { RetailerCard } from '../../components/home/RetailerCard'
import { WholesalerCard } from '../../components/home/WholesalerCard'
import PageTitle from '../../components/common/PageTitle'
import { useQuery } from '@tanstack/react-query'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { handleGetRequest } from '../../utils/api-handler.util'
import Pagination from '../../components/common/Pagination'
import ProductCard from '../../components/products/ProductCard'
import SeoHelmet from '../../components/common/SeoHelmet'

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams()
  const query = searchParams.get('q') || ''
  const [pages, updatePages] = useReducer((state, action) => ({ ...state, ...action }), {
    productsPage: 1,
    retailersPage: 1,
    wholesellersPage: 1,
  })
  const { data: searchResultsData } = useQuery({
    queryKey: QueryKeys.search({ q: query, ...pages }),
    queryFn: () => handleGetRequest('/search', { q: query, ...pages }),
  })

  const products = searchResultsData?.data?.products
  const retailers = searchResultsData?.data?.retailers
  const wholesellers = searchResultsData?.data?.wholesellers

  const renderResults = () => {
    if (!query) {
      return <div className="text-center text-gray-500 p-6">Please enter a search term above.</div>
    }

    const hasData = products?.data?.length || retailers?.data?.length || wholesellers?.data?.length
    if (!hasData) {
      return <div className="text-center text-gray-500 p-6">No results found for "{query}".</div>
    }

    return (
      <div className="space-y-8">
        {products.data?.length > 0 && (
          <section>
            <h2 className="text-xl font-semibold mb-4 border-b pb-2">Products</h2>
            <div className="grid grid-cols-1 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
              {products.data.map((product) => (
                <Link
                  key={product.id}
                  to={`/retailers/${product.shopSlug}/products/${product.productSlug}`}
                  style={{ textDecoration: 'none' }}
                >
                  <ProductCard product={product} catalogProducts={true} />
                </Link>
              ))}
            </div>
            <Pagination
              paginationMeta={products.meta}
              onPageChange={(page) => updatePages({ productsPage: page })}
            />
          </section>
        )}

        {retailers.data?.length > 0 && (
          <section>
            <h2 className="text-xl font-semibold mb-4 border-b pb-2">Retailers</h2>
            <div className="flex overflow-x-auto gap-x-4 scrollbar-hide py-4 pl-4 clip-padding-[0_0_0_1rem]">
              {retailers.data.map((retailer) => (
                <Link to={`/retailers/${retailer.shopSlug}/products`}>
                  <RetailerCard
                    key={`retailer-${retailer.id || retailer.retailerId}`}
                    retailer={retailer}
                  />
                </Link>
              ))}
            </div>
            <Pagination
              paginationMeta={retailers.meta}
              onPageChange={(page) => updatePages({ retailersPage: page })}
            />
          </section>
        )}

        {wholesellers.data?.length > 0 && (
          <section>
            <h2 className="text-xl font-semibold mb-4 border-b pb-2">Wholesalers</h2>
            <div className="flex overflow-x-auto gap-x-4 scrollbar-hide py-4 pl-4 clip-padding-[0_0_0_1rem]">
              {wholesellers.data.map((wholesaler) => (
                <WholesalerCard
                  key={`wholesaler-${wholesaler.wholesellerId || wholesaler.id}`}
                  wholesaler={wholesaler}
                />
              ))}
            </div>
            <Pagination
              paginationMeta={wholesellers.meta}
              onPageChange={(page) => updatePages({ wholesellersPage: page })}
            />
          </section>
        )}
      </div>
    )
  }

  return (
    <div className="main-container">
      <SeoHelmet
        title={query ? `Search Results for "${query}"` : 'Search'}
        description={
          query
            ? `Find products, retailers and wholesalers related to "${query}" on Sokooni marketplace. Discover quality items from trusted Eastleigh sellers.`
            : "Search for products, retailers and wholesalers on Sokooni marketplace. Find what you need from Eastleigh's best sellers."
        }
        keywords={
          query
            ? `${query}, search results, Eastleigh marketplace, Kenya shopping`
            : 'search, marketplace, Eastleigh, Kenya ecommerce'
        }
        url={query ? `/search?q=${encodeURIComponent(query)}` : '/search'}
      />
      <PageTitle>{query ? `Search Results for "${query}"` : 'Search'}</PageTitle>
      {renderResults()}
    </div>
  )
}

export default SearchResultsPage
